import React from 'react';
import { Button } from '@/components/ui/button';
import { CalendarCheck } from 'lucide-react';
import { useChatContext } from '@/hooks/useChatContext';

type FollowUpSuggestionsProps = {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  brandColor?: string;
};

// Utility functions moved to src/utils/followUpSuggestions.ts

// Export only the component
export const FollowUpSuggestions: React.FC<FollowUpSuggestionsProps> = ({
  suggestions,
  onSuggestionClick,
  brandColor = "#060520"
}) => {
  if (!suggestions || suggestions.length === 0) return null;

  const chatContext = useChatContext();
  const agent = chatContext?.agent;

  // Check if scheduling is enabled on the agent
  const schedulingEnabled = typeof agent?.skills_scheduling === 'object' && agent?.skills_scheduling !== null
    ? (agent.skills_scheduling as Record<string, unknown>).enabled as boolean
    : false;

  // Filter out booking-related suggestions if scheduling isn't enabled
  const filteredSuggestions = schedulingEnabled
    ? suggestions
    : suggestions.filter(suggestion =>
      !suggestion.toLowerCase().includes('book') &&
      !suggestion.toLowerCase().includes('slot') &&
      !suggestion.toLowerCase().includes('schedule') &&
      !suggestion.toLowerCase().includes('appointment')
    );

  if (filteredSuggestions.length === 0) return null;

  // No longer checking for embedBubble - show suggestions in all contexts
  console.log('Rendering follow-up suggestions:', filteredSuggestions);

  return (
    <div className="overflow-x-auto py-2"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }}>
      <div className="flex gap-2 justify-start">
        {filteredSuggestions.map((suggestion, index) => {
          // Special styling for "I've booked a slot" button
          const isBookingConfirmation = suggestion === "I've booked a slot";

          return (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => onSuggestionClick(suggestion)}
              className={`text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 whitespace-nowrap flex-shrink-0 ${isBookingConfirmation ? 'font-medium' : ''
                }`}
              style={{
                borderColor: isBookingConfirmation ? brandColor : `${brandColor}50`,
                color: brandColor,
                backgroundColor: isBookingConfirmation ? `${brandColor}10` : 'white'
              }}
            >
              {isBookingConfirmation && <CalendarCheck className="mr-1 h-4 w-4" />}
              {suggestion}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
